import { useCallback, useEffect, useState } from "react";

import api from "../../services/api";

// As permissões efetivas de quem está usando o sistema.
//
// Vêm do backend (`/permissions/mine`), que é a mesma fonte do `can()` que
// barra as rotas de verdade. Calcular isso no frontend a partir do perfil
// daria uma segunda régua, e as duas divergiriam no primeiro cargo novo.
//
// Guardadas em memória por montagem da página. Não vale a pena cachear em
// disco: permissão muda pouco, mas quando muda precisa valer no mesmo dia.
let cache = null;
let carregandoPromise = null;

export const limparCachePermissoes = () => {
  cache = null;
  carregandoPromise = null;
};

const usePermissions = () => {
  const [permissoes, setPermissoes] = useState(cache?.permissions || null);
  // Recorte de tickets do cargo ("company" | "team" | "queues" | "own"). Vem
  // na mesma resposta e era jogado fora; a lista de atendimentos precisa dele
  // para saber quando pode mostrar o que está fora das filas da pessoa.
  const [escopo, setEscopo] = useState(cache?.ticketScope || null);
  const [carregando, setCarregando] = useState(!cache);

  useEffect(() => {
    let vivo = true;

    if (cache) {
      setPermissoes(cache.permissions);
      setEscopo(cache.ticketScope);
      setCarregando(false);
      return () => {
        vivo = false;
      };
    }

    // Uma requisição só, mesmo com várias telas perguntando ao mesmo tempo.
    if (!carregandoPromise) {
      carregandoPromise = api
        .get("/permissions/mine")
        .then(({ data }) => {
          cache = {
            permissions: data?.permissions || [],
            ticketScope: data?.ticketScope || null,
            isActive: data?.isActive !== false
          };
          return cache;
        })
        .catch(() => {
          // Sem resposta, não dá para afirmar que a pessoa NÃO pode. Devolver
          // lista vazia trancaria o sistema inteiro por uma falha de rede.
          cache = { permissions: null, ticketScope: null, isActive: true };
          return cache;
        });
    }

    carregandoPromise.then((resultado) => {
      if (!vivo) return;
      setPermissoes(resultado.permissions);
      setEscopo(resultado.ticketScope);
      setCarregando(false);
    });

    return () => {
      vivo = false;
    };
  }, []);

  // `null` significa "não foi possível saber" — e aí não bloqueia. Quem barra
  // de verdade é o backend; esta camada existe para explicar, não para ser a
  // única defesa.
  const temPermissao = useCallback(
    (chave) => {
      if (!chave) return true;
      if (permissoes === null) return true;

      return permissoes.includes(chave);
    },
    [permissoes]
  );

  return { temPermissao, carregando, permissoes, ticketScope: escopo };
};

export default usePermissions;
